import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Shield, Mail, Lock, User, Eye, EyeOff, Loader2, AlertCircle, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { betaAPI } from '../services/api';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, loginWithGoogle, isAuthenticated, loading: authLoading } = useAuth();
  const { t, language } = useLanguage();
  
  const [mode, setMode] = useState(location.state?.mode === 'register' ? 'register' : 'login'); // 'login' or 'register'
  const [formData, setFormData] = useState({
    name: '',
    email: location.state?.email || '',
    password: '',
    betaCode: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(location.state?.error || '');
  const [betaEnabled, setBetaEnabled] = useState(false);

  // Check if beta is enabled
  useEffect(() => {
    const checkBetaSettings = async () => {
      try {
        const settings = await betaAPI.getSettings();
        setBetaEnabled(settings.is_active);
      } catch (error) {
        console.error('Failed to check beta settings:', error);
        setBetaEnabled(false);
      }
    };
    checkBetaSettings();
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (mode === 'login') {
        await login(formData.email, formData.password, formData.betaCode || null);
      } else {
        if (!formData.name.trim()) {
          throw new Error(t('nameRequired'));
        }
        await register(formData.name, formData.email, formData.password, formData.betaCode || null);
      }
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Pass beta code to Google login if provided
    loginWithGoogle(formData.betaCode || null);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex flex-col">
      {/* Header */}
      <header className="p-4 flex justify-between items-center">
        <Link to="/" className="flex items-center w-fit">
          <img src="/humanistix-ai-logo.png" alt="Humanistix AI" className="h-10 w-auto" />
        </Link>
        <LanguageSwitcher />
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* Title */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {mode === 'login' ? t('welcomeBack') : t('createAccount')}
              </h1>
              <p className="text-gray-600">
                {mode === 'login' 
                  ? t('loginDescription') 
                  : t('registerDescription')}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className={`rounded-lg p-4 mb-6 ${
                error === 'NOT_REGISTERED' 
                  ? 'bg-blue-50 border border-blue-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-start gap-3">
                  <AlertCircle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                    error === 'NOT_REGISTERED' ? 'text-blue-500' : 'text-red-500'
                  }`} />
                  <div className="flex-1">
                    {error === 'NOT_REGISTERED' ? (
                      <>
                        <p className="text-sm font-medium text-blue-800 mb-1">
                          {language === 'nl' 
                            ? 'Dit e-mailadres is nog niet geregistreerd' 
                            : 'This email address is not yet registered'}
                        </p>
                        <p className="text-sm text-blue-700 mb-2">
                          {language === 'nl'
                            ? 'U bevindt zich op de inlogpagina. Heeft u nog geen account?'
                            : 'You are on the login page. Don\'t have an account yet?'}
                        </p>
                        <Button
                          type="button"
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => { setMode('register'); setError(''); }}
                        >
                          {language === 'nl' ? 'Registreer hier →' : 'Register here →'}
                        </Button>
                      </>
                    ) : error === 'INVALID_PASSWORD' ? (
                      <>
                        <p className="text-sm font-medium text-red-800 mb-1">
                          {language === 'nl' 
                            ? 'Onjuist wachtwoord' 
                            : 'Incorrect password'}
                        </p>
                        <p className="text-sm text-red-700">
                          {language === 'nl'
                            ? 'Het wachtwoord dat u heeft ingevoerd is niet correct. Probeer het opnieuw.'
                            : 'The password you entered is incorrect. Please try again.'}
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-red-700">{error}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Beta Code Field - Shown at top for both login methods */}
            {betaEnabled && (
              <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <label className="block text-sm font-medium text-purple-700 mb-2">
                  {t('betaCode')} <span className="text-purple-400 text-xs">({t('optional')})</span>
                </label>
                <div className="relative">
                  <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-purple-400" />
                  <Input
                    type="text"
                    name="betaCode"
                    value={formData.betaCode}
                    onChange={handleInputChange}
                    placeholder="BETA2025"
                    className="pl-10 uppercase border-purple-200 focus:border-purple-400"
                    data-testid="beta-code-input"
                  />
                </div>
                <p className="text-xs text-purple-600 mt-1">{t('betaCodeHint')}</p>
              </div>
            )}

            {/* Google Login */}
            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleLogin}
              className="w-full py-6 mb-6 border-2 hover:bg-gray-50"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {t('continueWithGoogle')}
            </Button>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">{t('orContinueWith')}</span>
              </div>
            </div>

            {/* Email Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'register' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('fullName')}
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={t('yourName')}
                      className="pl-10"
                      data-testid="register-name-input"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('email')}
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    className="pl-10"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('password')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    required
                    minLength={6}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-500 hover:bg-blue-600 py-6"
                data-testid="submit-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : null}
                {mode === 'login' ? t('signIn') : t('createAccountBtn')}
              </Button>
            </form>

            {/* Toggle Mode */}
            <p className="text-center mt-6 text-gray-600">
              {mode === 'login' ? (
                <>
                  {t('noAccount')}{' '}
                  <button
                    type="button"
                    onClick={() => { setMode('register'); setError(''); }}
                    className="text-blue-500 hover:text-blue-600 font-medium"
                    data-testid="switch-to-register"
                  >
                    {t('signUp')}
                  </button>
                </>
              ) : (
                <>
                  {t('alreadyHaveAccount')}{' '}
                  <button
                    type="button"
                    onClick={() => { setMode('login'); setError(''); }}
                    className="text-blue-500 hover:text-blue-600 font-medium"
                    data-testid="switch-to-login"
                  >
                    {t('signIn')}
                  </button>
                </>
              )}
            </p>
          </div>

          {/* Info Text */}
          <p className="text-center text-sm text-gray-500 mt-6">
            {t('termsAgreement')}
          </p>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
